import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import * as yup from "yup";
import Header from "../../../Layouts/Components/Header";
import CustomButton from "../../shared/CustomButton";
import DirectoriesSettings from "./DirectoriesSettings";
import useSnackBar from "../../Hooks/useSnackBar";
import ScreenLoader from "../../shared/ScreenLoader";
import {
  updateChannel,
  getChannel,
  resetArchivalManagement,
  createChannel,
} from "../state/actions";

import usePrompt from "../../Hooks/usePrompt";
import SaveChangesModal from "../../shared/SaveChangesModal";
import MqSettings from "./MqSettings";
import MessagesFilesConverter from "./MessagesFilesConverter";

//Form validation
const validationSchema = yup.object().shape({
  messageMethod: yup.string().required("This field is mandatory"),
  incoming: yup.string().required("This field is mandatory"),
  outgoing: yup.string().required("This field is mandatory"),
  toSwift: yup.string().required("This field is mandatory"),
  msgHistory: yup.string().required("This field is mandatory"),
  standardProtocol: yup.string().required("This field is mandatory"),
  confirmationFile: yup.string().required("This field is mandatory"),
  posting: yup.string().required("This field is mandatory"),
  afterAMLFilter: yup.string().required("This field is mandatory"),
  logFile: yup.string().required("This field is mandatory"),
  //MQ Settings
  MQManager: yup.string().when("messageMethod", {
    is: "MQ__",
    then: yup.string().required("This field is mandatory"),
  }),
  MQServer: yup.string().when("messageMethod", {
    is: "MQ__",
    then: yup.string().required("This field is mandatory"),
  }),
  QIn: yup.string().when("messageMethod", {
    is: "MQ__",
    then: yup.string().required("This field is mandatory"),
  }),
  QOut: yup.string().when("messageMethod", {
    is: "MQ__",
    then: yup.string().required("This field is mandatory"),
  }),
  QToSwift: yup.string().when("messageMethod", {
    is: "MQ__",
    then: yup.string().required("This field is mandatory"),
  }),
  port: yup.string().when("messageMethod", {
    is: "MQ__",
    then: yup.string().required("This field is mandatory"),
  }),
});

// const dummyFormValues = {
//   messageMethod: "MQ",
//   incoming: "/dummy/path",
//   outgoing: "/dummy/path",
//   toSwift: "/dummy/path",
//   msgHistory: "/dummy/path",
//   standardProtocol: "All Formats",
//   confirmationFile: "/dummy/path",
//   posting: "/dummy/path",
// };

const SearchArchive = () => {
  const { processing, loadingLicense, channel } = useSelector(
    (state) => state.archivalManagement
  );
  const [createNew, setCreateNew] = useState(false);
  const dispatch = useDispatch();
  const { success, error, snackbar } = useSnackBar();
  const [touched, setTouched] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);

  const Navigate = useNavigate();
  const loadChannel = useCallback(
    () => dispatch(getChannel(error)),
    [dispatch, error]
  );
  const reset = useCallback(
    () => dispatch(resetArchivalManagement()),
    [dispatch]
  );
  const navigate = useCallback((location) => Navigate(location), [Navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      messageMethod: channel?.protocolType || "DIR_",
      incoming: channel?.directoryConfiguration?.incoming || "",
      outgoing: channel?.directoryConfiguration?.outgoing || "",
      toSwift: channel?.directoryConfiguration?.toSwift || "",
      msgHistory: channel?.directoryConfiguration?.msgHistory || "",
      standardProtocol: channel?.standardProtocol || "ALL",
      confirmationFile: channel?.directoryConfiguration?.confirmationFile || "",
      posting: channel?.directoryConfiguration?.posting || "",
      afterAMLFilter: channel?.directoryConfiguration?.afterAMLFilter || "",
      logFile: channel?.directoryConfiguration?.logFile || "",
      //Messages Files Converter
      folder: channel?.directoryConfiguration?.folder || "",
      file: channel?.directoryConfiguration?.file || "",
      messageInput: channel?.directoryConfiguration?.messageInput || "",
      messageOutput: channel?.directoryConfiguration?.messageOutput || "",
      messageLog: channel?.directoryConfiguration?.messageLog || "",
      //MQ Settings
      MQManager: channel?.MQSettings?.MQManager || "",
      MQServer: channel?.MQSettings?.MQServer || "",
      QIn: channel?.MQSettings?.QIn || "",
      QOut: channel?.MQSettings?.QOut || "",
      QToSwift: channel?.MQSettings?.QToSwift || "",
      port: channel?.MQSettings?.port || "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const data = {
        protocolType: values.messageMethod,
        connectionConfiguration: null,
        standardProtocol: values.standardProtocol,
        directoryConfiguration: {
          outgoing: values.outgoing,
          incoming: values.incoming,
          toSwift: values.toSwift,
          msgHistory: values.msgHistory,
          afterAMLFilter: values.afterAMLFilter,
          confirmationFile: values.confirmationFile,
          logFile: values.logFile,
          posting: values.posting,
        },
      };
      if (!createNew)
        dispatch(updateChannel(data, success, error)).then(() => {
          setTouched(false);
          setIsConfirmationModalOpen(false);
        });
      else
        dispatch(createChannel(data, success, error)).then(() => {
          setTouched(false);
          setIsConfirmationModalOpen(false);
          setCreateNew(false);
        });
    },
  });

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (nextLocation.pathname === "/admin-tool/search-archive") return true;
      if (touched) {
        setPendingLocation(nextLocation.pathname);
        setIsConfirmationModalOpen(true);
        return false;
      }
      return true;
    },
    [touched]
  );

  //close navigation blocker handler
  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setPendingLocation(null);
  };

  //discard changes action handler
  const handleDiscardChanges = () => {
    setIsConfirmationModalOpen(false);
    setTouched(false);
  };

  //navigate to pendingLocation effect
  useEffect(() => {
    if (pendingLocation && !touched) navigate(pendingLocation);
  }, [navigate, pendingLocation, touched]);

  useEffect(() => {
    loadChannel();
    return () => {
      reset();
    };
  }, [loadChannel, reset]);

  //Navigation blocker
  usePrompt(handleBlockedNavigation, touched);

  // console.log(formik.errors);

  return (
    <>
      {snackbar}

      <SaveChangesModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        processing={processing}
        discardChanges={handleDiscardChanges}
        saveChanges={formik.handleSubmit}
      />

      <Header headLine="Search & Archive">
        <CustomButton
          processing={processing}
          text="Update Settings"
          type="update"
          disabled={!touched}
          action={formik.handleSubmit}
        />
      </Header>
      {loadingLicense ? (
        <ScreenLoader />
      ) : !channel && !createNew ? (
        <Box p={4}>
          <CustomButton
            processing={false}
            text="Create channel"
            type="update"
            disabled={false}
            action={() => setCreateNew(true)}
          />
        </Box>
      ) : (
        <Grid
          container
          justifyContent="center"
          sx={{
            background: "#F8FAFB",
            padding: "30px",
          }}
          rowSpacing={4}
        >
          <Grid item xs={12}>
            <DirectoriesSettings
              formik={formik}
              touched={touched}
              setTouched={setTouched}
            />
          </Grid>

          {formik.values.messageMethod === "MQ__" ? (
            <Grid item xs={12}>
              <MqSettings
                formik={formik}
                touched={touched}
                setTouched={setTouched}
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <MessagesFilesConverter
              formik={formik}
              touched={touched}
              setTouched={setTouched}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default SearchArchive;
